import type {
  DraftSubTradeRequest,
  PortfolioSubAccount,
} from '@ovComponents/4-module/widgets/portfolio/components/interfaces';
import type { WeightDiffDetailRow } from '../../types/snapshot.types';

export type SnapshotSubAccountFilterOptions = {
  includeInactiveSubAccounts?: boolean;
};

/** Hide inactive sleeves and zero-activity columns from the audit portfolio grid. */
export const shouldShowSnapshotSubAccount = (
  subAccount: PortfolioSubAccount,
  diffRows: WeightDiffDetailRow[],
  draftTrades: Map<string, DraftSubTradeRequest>,
  options: SnapshotSubAccountFilterOptions = {},
): boolean => {
  if (subAccount.inactive) {
    return Boolean(options.includeInactiveSubAccounts);
  }

  const hasBalance =
    subAccount.marketValueCents > 0 ||
    subAccount.expectedCashCents > 0 ||
    subAccount.cashCents > 0;

  if (hasBalance) {
    return true;
  }

  const hasTradeActivity = [...draftTrades.values()].some(
    (trade) => trade.subAccountId === subAccount.id,
  );

  if (hasTradeActivity) {
    return true;
  }

  return diffRows.some((row) =>
    row.subAccountRows.some(
      (entry) =>
        entry.subAccountId === subAccount.id &&
        (entry.currentCents !== 0 || entry.deltaCents !== 0),
    ),
  );
};

export const filterSnapshotSubAccounts = <
  T extends {
    subAccounts: PortfolioSubAccount[];
    securities: Array<{ subAccounts: PortfolioSubAccount[] }>;
  },
>(
  snapshot: T,
  diffRows: WeightDiffDetailRow[],
  draftTrades: Map<string, DraftSubTradeRequest>,
  options: SnapshotSubAccountFilterOptions = {},
): T => {
  const visibleSubAccounts = snapshot.subAccounts.filter((subAccount) =>
    shouldShowSnapshotSubAccount(subAccount, diffRows, draftTrades, options),
  );
  const visibleSubAccountIds = new Set(
    visibleSubAccounts.map((subAccount) => subAccount.id),
  );

  return {
    ...snapshot,
    subAccounts: visibleSubAccounts,
    securities: snapshot.securities.map((security) => ({
      ...security,
      subAccounts: security.subAccounts.filter((subAccount) =>
        visibleSubAccountIds.has(subAccount.id),
      ),
    })),
  };
};
